import React, { useState, useEffect, useRef } from 'react';
import '../styles/Projects.css';
import Card from './Card';
import Particles from './Particles';

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    const element = document.getElementById('projects');
    if (element) observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const projects = [
    {
      title: 'DataPilot AI - BI Dashboard',
      description: 'AI-powered business intelligence platform with conversational analytics, automated dataset discovery, and real-time visualizations. Features multi-provider AI routing (Google Gemini, Groq, OpenAI) with enterprise security (OWASP compliance) and interactive dashboard generation.',
      tech: ['React.js', 'Node.js', 'AI/ML', 'OpenAI', 'Express', 'Tailwind'],
      github: 'https://github.com/Prajeethv21',
      link: 'https://datapilot.prajeethv.dev/',
    },
    {
      title: 'PaperLens AI - Research Paper Reviewer',
      description: 'RAG-powered academic paper analysis with LLM-based peer review and bias detection. Features ChromaDB vector search, multi-reviewer simulation, research gap analysis, and acceptance predictions with enterprise-grade OWASP security.',
      tech: ['React.js', 'Node.js', 'FastAPI', 'OpenAI', 'LangChain', 'ChromaDB'],
      github: 'https://github.com/Prajeethv21',
      link: 'https://paperlens.prajeethv.dev/',
    },
    {
      title: 'Vidya AI - Educational App',
      description: 'Patent-granted AI-based mobile application built with FlutterFlow designed to personalize student learning experiences and help underprivileged students. Patent No: 202244081494 A.',
      tech: ['FlutterFlow', 'AI/ML', 'Firebase', 'Mobile Dev'],
      github: 'https://github.com/Prajeethv21',
    },
    {
      title: 'Forensic Crime Detection Hit-and-Run System',
      description: 'Developed a computer vision–based forensic system using Python and OpenCV to analyze vehicle images for hit-and-run investigations. The project captures and compares pre- and post-incident images using edge detection and image differencing to identify structural damage and assist in vehicle identification.',
      tech: ['Python', 'YOLOv3', 'OpenCV', 'SQLite'],
      github: 'https://github.com/Prajeethv21',
    },
    {
      title: 'LPRS - License Plate Recognition',
      description: 'Real-time Flask web application for automated vehicle monitoring with MySQL database. Features live camera feed and OCR text extraction using Tesseract.',
      tech: ['Python', 'Flask', 'OpenCV', 'Tesseract', 'MySQL'],
      github: 'https://github.com/Prajeethv21',
    },
  ];

  // 1 card per slide → 6 slides total
  const totalSlides = projects.length;

  const scrollToSlide = (index) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: index * scrollRef.current.clientWidth,
        behavior: 'smooth',
      });
    }
    setCurrentSlide(index);
  };

  const nextSlide = () => scrollToSlide((currentSlide + 1) % totalSlides);
  const prevSlide = () => scrollToSlide((currentSlide - 1 + totalSlides) % totalSlides);

  // Build single card slides
  const slides = [];
  for (let i = 0; i < projects.length; i += 1) {
    slides.push([projects[i]]);
  }

  return (
    <div className={`projects-section ${isVisible ? 'visible' : ''}`} id="projects">
      <div className="projects-background">
        <Particles
          particleColors={['#ffffff']}
          particleCount={100}
          particleSpread={10}
          speed={0.05}
          particleBaseSize={80}
          moveParticlesOnHover={false}
          alphaParticles={false}
          disableRotation={false}
          pixelRatio={1}
        />
      </div>
      <div className="container">
        <h2 className="section-title">
          <span className="title-number">04.</span>
          Things I've Built
        </h2>

        <div className="projects-carousel">
          <button className="carousel-arrow carousel-arrow-left" onClick={prevSlide}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div className="carousel-scroll" ref={scrollRef}>
            {slides.map((pair, slideIdx) => (
              <div className="carousel-slide" key={slideIdx}>
                {pair.map((project, idx) => (
                  <div className="carousel-card-box" key={idx}>
                    <Card
                      title={project.title}
                      description={project.description}
                      githubLink={project.github}
                      websiteLink={project.link}
                    >
                      <div className="card-tech-tags">
                        {project.tech.map((tech, i) => (
                          <span key={i} className="card-tech-tag">{tech}</span>
                        ))}
                      </div>
                      <a href={project.link || project.github} target="_blank" rel="noopener noreferrer" className="see-more">
                        View Project
                      </a>
                    </Card>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <button className="carousel-arrow carousel-arrow-right" onClick={nextSlide}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        <div className="carousel-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => scrollToSlide(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Projects;
